import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { getCompanyTypes, getForCompleteCompanyDetailsForm } from 'core'
import saveForCompanyDetails, {
  CompanyDetailsDataForSave,
} from 'core/application/adminDashboard/companySettings/companyDetails/saveForCompanyDetails'

import {
  formatOnlyChars,
  identificationNumberRegex,
  phoneNumberRegex,
  zipNumberRegex,
} from 'common/components/utils/InputUtils'
import { filterByKeyAndValue } from 'common/components/utils/SelectFilterUtils'

import {
  Button,
  Col,
  DefaultInputV2,
  DefaultSelect,
  InputMask,
  Option,
  Row,
  Skeleton,
  Tooltip,
} from 'common/components'
import { formatMonthYear } from 'common/dateFormatters'
import { useCommandRequest, useQueryRequest } from 'common/hooks'
import { ROUTE_PATHS } from 'common/routePaths'

const Input = DefaultInputV2

const formValidator = (translate: any) =>
  Yup.object({
    name: Yup.string().trim().required(translate('general.required')),
    addressLine1: Yup.string().trim().required(translate('general.required')),
    addressLine2: Yup.string()
      .nullable()
      .notRequired()
      .trim()
      .transform((value) => (value && value.length > 0 ? value : null)),
    city: Yup.string()
      .trim()
      .required(translate('general.required'))
      .matches(formatOnlyChars, translate('admin-signup.company-details.errors.company-name-regex')),
    stateId: Yup.string().trim().required(translate('general.required')),
    zipCode: Yup.string()
      .trim()
      .required(translate('general.required'))
      .matches(zipNumberRegex, translate('admin-signup.company-details.errors.zip-number-regex')),
    mainPhoneNumber: Yup.string()
      .required(translate('general.required'))
      .matches(phoneNumberRegex, translate('admin-signup.company-details.errors.phone-number-regex')),
    ein: Yup.string()
      .trim()
      .nullable()
      .required(translate('general.required'))
      .matches(identificationNumberRegex, translate('admin-signup.company-details.errors.identification-number-regex')),
  })

const CompanyDetailsForm = () => {
  const { t: translate } = useTranslation()
  const history = useHistory()

  const [initialValues] = useQueryRequest(null, () => getForCompleteCompanyDetailsForm())
  const [companyTypes] = useQueryRequest([], () => getCompanyTypes())
  const [sendCommand, isCommandLoading] = useCommandRequest()

  const onSubmit = async (values: any) => {
    const dataForSubmit: CompanyDetailsDataForSave = {
      name: values.name,
      addressLine1: values.addressLine1,
      addressLine2: values.addressLine2 ?? initialValues.addressLine2,
      city: values.city,
      stateId: values.stateId,
      zipCode: values.zipCode,
      mainPhoneNumber: values.mainPhoneNumber,
      companyTypeId: values.companyTypeId,
      ein: values.ein,
    }

    const result = await sendCommand(() => saveForCompanyDetails(dataForSubmit))
    if (result.completed) {
      history.push(`${ROUTE_PATHS.COMPANY_SETTINGS}`)
    }
  }

  return (
    <Skeleton loading={!initialValues}>
      {initialValues && (
        <Formik
          enableReinitialize={true}
          validationSchema={formValidator(translate)}
          initialValues={initialValues}
          onSubmit={onSubmit}
        >
          {(formik) => (
            <form onSubmit={formik.handleSubmit}>
              <Row>
                <Col span={24} className="flex align-center text-semi-bold">
                  {translate('admin-dashboard.company-settings.company-details.title')}
                </Col>
              </Row>

              <Row gutter={24}>
                <Col className="pt-24" xs={24} sm={12} lg={24} xl={12}>
                  <Input
                    name="name"
                    label={translate('admin-dashboard.company-settings.company-details.labels.company-name-short')}
                  />
                </Col>
                {/*<Tooltip*/}
                {/*  title={translate('admin-dashboard.company-settings.company-details.cannot-edit-tooltip')}*/}
                {/*  position="left"*/}
                {/*>*/}
                <Col className="pt-24" xs={24} sm={12} lg={24} xl={12}>
                  <Input
                    name="ein"
                    defaultValue=""
                    // disabled
                    label={translate('admin-dashboard.company-settings.company-details.labels.ein')}
                  />
                </Col>
                {/*</Tooltip>*/}
              </Row>

              <Row className="mt-24" gutter={24}>
                <Tooltip
                  title={translate('admin-dashboard.company-settings.company-details.cannot-edit-tooltip')}
                  position="left"
                >
                  <Col xs={24} sm={12}>
                    <DefaultSelect name={'companyTypeId'} items={companyTypes} />
                  </Col>
                </Tooltip>
              </Row>

              <Row className="mt-40" gutter={24}>
                <Col xs={12}>
                  <Input
                    name="addressLine1"
                    label={translate('admin-dashboard.company-settings.company-details.labels.address-1')}
                  />
                </Col>
                <Col xs={12}>
                  <Input
                    name="addressLine2"
                    label={translate('admin-dashboard.company-settings.company-details.labels.address-2')}
                  />
                </Col>
              </Row>

              <Row className="mt-24" gutter={24}>
                <Col xs={12}>
                  <Input
                    name="city"
                    label={translate('admin-dashboard.company-settings.company-details.labels.city')}
                  />
                </Col>
                <Col xs={12}>
                  <Row gutter={24}>
                    <Col span={12}>
                      <DefaultSelect
                        name="stateId"
                        defaultValue={[initialValues.stateId]}
                        label={translate('admin-dashboard.company-settings.company-details.labels.state')}
                        filterOption={filterByKeyAndValue}
                        showSearch
                      >
                        {initialValues.availableStates.map(({ id, name }) => (
                          <Option key={id} value={id}>
                            {id}
                          </Option>
                        ))}
                      </DefaultSelect>
                    </Col>
                    <Col span={12}>
                      <InputMask
                        name="zipCode"
                        mask="00000"
                        label={translate('admin-dashboard.company-settings.company-details.labels.zip')}
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>

              <Row className="mt-40">
                <Col xs={24} sm={12}>
                  <InputMask
                    mask="(000) 000-0000"
                    name="mainPhoneNumber"
                    placeholder="(555) 123-4567"
                    label={translate('admin-dashboard.company-settings.company-details.labels.phone')}
                  />
                </Col>
              </Row>

              {initialValues.groupHealthPlan?.startDate && (
                <Row className="mt-40">
                  <Tooltip
                    title={translate('admin-dashboard.company-settings.company-details.cannot-edit-tooltip')}
                    position="left"
                  >
                    <Col xs={24} sm={12}>
                      <Input
                        value={formatMonthYear(initialValues.groupHealthPlan.startDate)}
                        name="groupHealthPlan.startDate"
                        disabled
                        label={translate('admin-dashboard.company-settings.company-details.labels.start-month')}
                      />
                    </Col>
                  </Tooltip>
                </Row>
              )}

              {initialValues.groupHealthPlan?.insuranceCarrier && initialValues.groupHealthPlan?.insurancePlan && (
                <Row className="flex fd-row mt-24 mb-24" gutter={24}>
                  <Tooltip
                    title={translate('admin-dashboard.company-settings.company-details.cannot-edit-tooltip')}
                    position="left"
                  >
                    <Col span={12}>
                      <Input
                        name="groupHealthPlan.insuranceCarrier"
                        disabled
                        label={translate('admin-dashboard.company-settings.company-details.labels.insurance-carrier')}
                      />
                    </Col>
                  </Tooltip>
                  <Tooltip
                    title={translate('admin-dashboard.company-settings.company-details.cannot-edit-tooltip')}
                    position="left"
                  >
                    <Col span={12}>
                      <Input
                        name="groupHealthPlan.insurancePlan"
                        disabled
                        label={translate('admin-dashboard.company-settings.company-details.labels.insurance-plan')}
                      />
                    </Col>
                  </Tooltip>
                </Row>
              )}

              <Row justify="end">
                <Col span={24} className="flex justify-end">
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isCommandLoading}
                    disabled={!formValidator(translate).isValidSync(formik.values) || isCommandLoading}
                  >
                    {translate('general.save-changes')}
                  </Button>
                </Col>
              </Row>
            </form>
          )}
        </Formik>
      )}
    </Skeleton>
  )
}

export default CompanyDetailsForm
